import { z } from "zod";

import { ModelProviderSchema } from "./agent.schemas";
import { optionalQueryParam } from "./common.schemas";
import { GoldenRecordedToolCallSchema } from "./golden-conversation.schemas";

// ============================================================================
// PROMPT EVAL RUNS — the matrix (Prompt Lab phase 3)
//
// A run compares prompt VARIANTS against GOLDEN CONVERSATIONS: every cell is
// "given this conversation's prefix, generate the next agent message under
// this candidate prompt version, then judge it against the golden one". Tools
// execute live, so a run costs real money and carries an optional budget cap.
//
// These schemas are the single source for both the public v1 contract and the
// internal console door — same shapes, same idiom as phase 2's.
// ============================================================================

export const PromptEvalRunStatusSchema = z.enum([
  "QUEUED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "ABORTED"
]);

export const PromptEvalAbortReasonSchema = z.enum(["USER_REQUESTED", "BUDGET_CAP"]);

export const PromptEvalCaseStatusSchema = z.enum([
  "PENDING",
  "RUNNING",
  "JUDGED",
  "FAILED",
  "SKIPPED"
]);

export const PromptEvalVerdictSchema = z.enum(["PASS", "FAIL", "INCONCLUSIVE"]);

// ---------------------------------------------------------------------------
// The run's snapshots
// ---------------------------------------------------------------------------

/// One prompt version entered into the comparison, as it stood at creation.
/// `promptText` is a copy: saving to the variant afterwards does not change
/// what the run says it compared.
export const PromptEvalRunCandidateSchema = z.object({
  versionId: z.string(),
  variantName: z.string(),
  promptText: z.string()
});

/// The checkpoints one conversation contributes, expanded and validated at
/// creation — never the raw request.
export const PromptEvalRunSelectionEntrySchema = z.object({
  conversationId: z.string(),
  turnIndexes: z.array(z.number().int())
});

/// The judge settings the run ran under, resolved to concrete values.
export const PromptEvalJudgeConfigSchema = z.object({
  model: z.string(),
  provider: ModelProviderSchema,
  repetitions: z.number().int()
});

// ---------------------------------------------------------------------------
// Rollup
// ---------------------------------------------------------------------------

export const PromptEvalRollupCostSchema = z.object({
  totalUsdTenThousandths: z.number().int(),
  generationUsdTenThousandths: z.number().int(),
  judgeUsdTenThousandths: z.number().int()
});

export const PromptEvalRollupConversationSchema = z.object({
  conversationId: z.string(),
  caseCount: z.number().int(),
  meanScore: z.number().nullable(),
  passRate: z.number().nullable()
});

/// One variant's aggregate. `meanScore` and `passRate` are null — never 0 —
/// when nothing scored: no measurement and a measured zero are opposite facts.
/// `deltaVsBaseline` is ABSENT on a run that chose no baseline, so a
/// variants-only result table has no delta column at all.
export const PromptEvalRollupVariantSchema = z.object({
  versionId: z.string(),
  name: z.string(),
  isBaseline: z.boolean(),
  caseCount: z.number().int(),
  scoredCaseCount: z.number().int(),
  failedCaseCount: z.number().int(),
  inconclusiveScoreCount: z.number().int(),
  meanScore: z.number().nullable(),
  passRate: z.number().nullable(),
  deltaVsBaseline: z.number().optional(),
  conversations: z.array(PromptEvalRollupConversationSchema),
  cost: PromptEvalRollupCostSchema
});

export const PromptEvalRunRollupSchema = z.object({
  variants: z.array(PromptEvalRollupVariantSchema),
  cost: PromptEvalRollupCostSchema
});

// ---------------------------------------------------------------------------
// The run
// ---------------------------------------------------------------------------

/// Accrued spend, in ten-thousandths of a USD (1500 = $0.15) — the unit every
/// monetary figure in this API uses. Token counts are plain numbers: the
/// column is BigInt, and it narrows at the mapper because JSON cannot carry a
/// BigInt at all.
export const PromptEvalRunCostSchema = z.object({
  totalUsdTenThousandths: z.number().int(),
  generationUsdTenThousandths: z.number().int(),
  judgeUsdTenThousandths: z.number().int(),
  inputTokens: z.number().int(),
  outputTokens: z.number().int()
});

export const PromptEvalRunSchema = z.object({
  id: z.string().uuid(),
  agentId: z.string().uuid(),
  name: z.string().nullable(),
  status: PromptEvalRunStatusSchema,
  /// Set only when status is ABORTED.
  abortReason: PromptEvalAbortReasonSchema.nullable(),
  candidates: z.array(PromptEvalRunCandidateSchema),
  /// The candidate deltas are measured against. Null = a variants-only run.
  baselineVersionId: z.string().uuid().nullable(),
  selection: z.array(PromptEvalRunSelectionEntrySchema),
  judgeConfig: PromptEvalJudgeConfigSchema,
  /// Null until the run reaches a terminal status.
  rollup: PromptEvalRunRollupSchema.nullable(),
  budgetCapUsdTenThousandths: z.number().int().nullable(),
  cost: PromptEvalRunCostSchema,
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

// ---------------------------------------------------------------------------
// Cases and scores
// ---------------------------------------------------------------------------

export const PromptEvalScoreSchema = z.object({
  id: z.string().uuid(),
  caseId: z.string().uuid(),
  /// "golden_match" (built-in) or a checkpoint's own criterion name.
  criterion: z.string(),
  /// 0..1. The judge is asked on a 0-10 scale and the answer divided.
  score: z.number(),
  verdict: PromptEvalVerdictSchema,
  reasoning: z.string(),
  model: z.string(),
  repetition: z.number().int(),
  createdAt: z.coerce.date()
});

/// The golden turn and the prefix leading to it, frozen when the run was
/// created. Editing the conversation afterwards never touches this — that is
/// what makes a past run still mean what it said.
export const PromptEvalGoldenSnapshotSchema = z.object({
  content: z.string(),
  toolCalls: z.array(GoldenRecordedToolCallSchema),
  prefix: z.array(z.object({ role: z.enum(["USER", "AGENT"]), content: z.string() }))
});

/// What the candidate prompt actually produced, tool calls included.
export const PromptEvalCaseCandidateSchema = z.object({
  content: z.string(),
  toolCalls: z.array(GoldenRecordedToolCallSchema),
  /// The ephemeral emulator session it ran in — openable in the emulator UI.
  sessionId: z.string()
});

export const PromptEvalCaseSchema = z.object({
  id: z.string().uuid(),
  runId: z.string().uuid(),
  versionId: z.string().uuid(),
  /// The variant name this version belongs to, resolved off the run's own
  /// snapshot; null if the run no longer lists the version.
  variantName: z.string().nullable(),
  conversationId: z.string().uuid(),
  turnIndex: z.number().int(),
  status: PromptEvalCaseStatusSchema,
  /// Set only when the cell FAILED.
  failureReason: z.string().nullable(),
  latencyMs: z.number().int().nullable(),
  costUsdTenThousandths: z.number().int(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date()
});

/// A results-table cell: the case with its judge verdicts, without the bulky
/// golden/candidate bodies.
export const PromptEvalCaseWithScoresSchema = PromptEvalCaseSchema.extend({
  scores: z.array(PromptEvalScoreSchema)
});

/// One cell in full: golden versus candidate, side by side.
export const PromptEvalCaseDetailSchema = PromptEvalCaseSchema.extend({
  goldenSnapshot: PromptEvalGoldenSnapshotSchema,
  candidate: PromptEvalCaseCandidateSchema.nullable(),
  /// The prompt text this cell ran under, off the run's snapshot.
  promptText: z.string().nullable(),
  scores: z.array(PromptEvalScoreSchema)
});

// ---------------------------------------------------------------------------
// Path params. Ids are deliberately NOT `.uuid()`: an unknown or malformed id
// must answer "not found", not a format error (the read adapters shape-guard
// before the @db.Uuid compare). Same precedent as phase 2's conversationId.
// ---------------------------------------------------------------------------

export const PromptEvalRunIdParamSchema = z.object({
  runId: z.string().min(1)
});

export const PromptEvalRunCaseParamSchema = z.object({
  runId: z.string().min(1),
  caseId: z.string().min(1)
});

// ---------------------------------------------------------------------------
// Create / preview
// ---------------------------------------------------------------------------

/// Spec §5.5: a per-run judge override, capped at 5 repetitions. Above that
/// the API refuses rather than quietly clamping — a caller who asked for 20
/// wants to know they did not get them.
export const PromptEvalJudgeOverrideSchema = z
  .object({
    model: z.string().trim().min(1).optional(),
    provider: ModelProviderSchema.optional(),
    repetitions: z.number().int().min(1).max(5).optional()
  })
  .strict();

/// Which checkpoints of one conversation to run. Absent from the list = every
/// checkpoint that conversation carries.
export const PromptEvalCheckpointSelectionSchema = z
  .object({
    conversationId: z.string().min(1),
    turnIndexes: z.array(z.number().int().min(0)).min(1)
  })
  .strict();

const promptEvalRunPlanFields = {
  /// The golden conversations to run. All must be READY and belong to one agent.
  conversationIds: z.array(z.string().min(1)).min(1),
  checkpoints: z.array(PromptEvalCheckpointSelectionSchema).optional(),
  /// Variant refs (name, id, or "main") whose TIPS are the candidates.
  variants: z.array(z.string().trim().min(1)).min(1),
  /// "main", "none", or a variant ref. Absent = none, and then no deltas.
  baseline: z.string().trim().min(1).optional(),
  judge: PromptEvalJudgeOverrideSchema.optional()
};

export const CreatePromptEvalRunBodySchema = z
  .object({
    ...promptEvalRunPlanFields,
    name: z.string().trim().min(1).max(200).optional(),
    /// Guardrail: the run aborts once accrued cost reaches this, in
    /// ten-thousandths of a USD. Absent = uncapped.
    budgetCapUsdTenThousandths: z.number().int().min(1).optional()
  })
  .strict();

export const PreviewPromptEvalRunBodySchema = z.object(promptEvalRunPlanFields).strict();

/// The created run, plus anything the caller should know about it — a matrix
/// over the warning threshold, or a run that could not be queued.
export const CreatePromptEvalRunResponseSchema = z.object({
  run: PromptEvalRunSchema,
  warnings: z.array(z.string())
});

export const PreviewPromptEvalRunResponseSchema = z.object({
  agentId: z.string().uuid(),
  caseCount: z.number().int(),
  candidates: z.array(PromptEvalRunCandidateSchema),
  selection: z.array(PromptEvalRunSelectionEntrySchema),
  warnings: z.array(z.string())
});

// ---------------------------------------------------------------------------
// Read / abort
// ---------------------------------------------------------------------------

export const ListPromptEvalRunsParamsSchema = z.object({
  /// `?agentId=` (empty value) means "no filter", not a format error.
  agentId: optionalQueryParam(z.string().uuid())
});

export const ListPromptEvalRunsResponseSchema = z.array(PromptEvalRunSchema);

export const GetPromptEvalRunResponseSchema = PromptEvalRunSchema;

export const AbortPromptEvalRunResponseSchema = PromptEvalRunSchema;

export const GetPromptEvalRunResultsResponseSchema = z.object({
  run: PromptEvalRunSchema,
  /// Null until the run reaches a terminal status — a RUNNING run has cells
  /// but no aggregate yet.
  rollup: PromptEvalRunRollupSchema.nullable(),
  cases: z.array(PromptEvalCaseWithScoresSchema)
});

export const GetPromptEvalCaseResponseSchema = PromptEvalCaseDetailSchema;
